/**
 * 返信ダイアログ
 */

import { Loader2, Mail } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

interface ReplyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contactEmail: string;
  contactSubject: string;
  onSubmit: (subject: string, message: string) => Promise<boolean>;
  isSubmitting: boolean;
}

export function ReplyDialog({
  open,
  onOpenChange,
  contactEmail,
  contactSubject,
  onSubmit,
  isSubmitting,
}: ReplyDialogProps) {
  const [subject, setSubject] = useState(`Re: ${contactSubject}`);
  const [message, setMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) return;
    await onSubmit(subject.trim(), message.trim());
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSubject(`Re: ${contactSubject}`);
      setMessage("");
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            返信を送信
          </DialogTitle>
          <DialogDescription>{contactEmail} へ返信メールを送信します</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>件名</Label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="メールの件名"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>本文</Label>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="返信内容を入力..."
                rows={10}
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isSubmitting}
            >
              キャンセル
            </Button>
            <Button type="submit" disabled={isSubmitting || !subject.trim() || !message.trim()}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              送信
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
